<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

const store = useStore();

const templates = ref([]);
const isLoading = ref(false);
const isSyncing = ref(false);
const isSaving = ref(false);
const showCreateForm = ref(false);
const editingTemplate = ref(null);
const searchQuery = ref('');
const categoryFilter = ref('all');
const showDeleteConfirm = ref(false);
const deleteTarget = ref(null);
const mediaFileInput = ref(null);
const mediaFile = ref(null);

// Create form fields
const form = ref({
  elementName: '',
  languageCode: 'es',
  category: 'UTILITY',
  templateType: 'TEXT',
  content: '',
  example: '',
  enableSample: true,
  header: '',
  footer: '',
  vertical: '',
  exampleHeader: '',
  mediaUrl: '',
});

// Buttons
const buttons = ref([]);

const LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
];

const CATEGORY_OPTIONS = [
  { value: 'UTILITY', label: 'Utilidad' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Autenticación' },
];

const BUTTON_TYPE_OPTIONS = [
  { value: 'QUICK_REPLY', label: 'Respuesta rápida' },
  { value: 'URL', label: 'Enlace URL' },
  { value: 'PHONE_NUMBER', label: 'Teléfono' },
  { value: 'COPY_CODE', label: 'Copiar código' },
];

const TEXT_TEMPLATE_TYPE_OPTIONS = [{ value: 'TEXT', label: 'Texto' }];
const ALL_TEMPLATE_TYPE_OPTIONS = [
  ...TEXT_TEMPLATE_TYPE_OPTIONS,
  { value: 'IMAGE', label: 'Imagen' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Documento' },
];

const isCloudProvider = computed(
  () => props.inbox.provider === 'whatsapp_cloud'
);

const providerLabel = computed(() =>
  isCloudProvider.value ? 'Meta Cloud API' : 'Gupshup'
);

const sectionSubtitle = computed(
  () =>
    `Gestiona las plantillas de mensajes para tu canal de ${providerLabel.value}. ` +
    'Las plantillas deben ser aprobadas por Meta antes de su uso.'
);

const categoryOptions = computed(() =>
  isCloudProvider.value
    ? CATEGORY_OPTIONS.filter(option => option.value !== 'AUTHENTICATION')
    : CATEGORY_OPTIONS
);

const templateTypeOptions = computed(() => ALL_TEMPLATE_TYPE_OPTIONS);

const buttonTypeOptions = computed(() =>
  isCloudProvider.value
    ? BUTTON_TYPE_OPTIONS.filter(option => option.value !== 'COPY_CODE')
    : BUTTON_TYPE_OPTIONS
);

const showMediaUrlField = computed(() =>
  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.value.templateType)
);

const showHeaderTextField = computed(() => !showMediaUrlField.value);

const bodyVariables = computed(() => {
  const matches = form.value.content.matchAll(/\{\{\s*(\d+)\s*\}\}/g);
  return [...new Set([...matches].map(match => match[1]))];
});

const bodyVariableLabel = variable => `{{${variable}}}`;

const acceptedMediaTypes = computed(() => {
  const acceptedTypes = {
    IMAGE: 'image/jpeg,image/png',
    VIDEO: 'video/mp4',
    DOCUMENT: 'application/pdf',
  };
  return acceptedTypes[form.value.templateType] || '';
});

const filteredTemplates = computed(() => {
  let result = templates.value;
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(t =>
      (t.name || t.elementName || '').toLowerCase().includes(query)
    );
  }
  if (categoryFilter.value !== 'all') {
    result = result.filter(t => t.category === categoryFilter.value);
  }
  return result;
});

// Helper functions (declared before usage to satisfy no-use-before-define)
function getBodyText(template) {
  const body = (template.components || []).find(c => c.type === 'BODY');
  return body?.text || template.bodyText || '';
}

function getHeaderText(template) {
  const header = (template.components || []).find(c => c.type === 'HEADER');
  return header?.text || '';
}

function getFooterText(template) {
  const footer = (template.components || []).find(c => c.type === 'FOOTER');
  return footer?.text || '';
}

function getHeaderExample(template) {
  const header = (template.components || []).find(c => c.type === 'HEADER');
  if (header?.example?.header_handle)
    return header.example.header_handle.join(', ');
  return '';
}

function getTemplateType(template) {
  const header = (template.components || []).find(c => c.type === 'HEADER');
  return header?.format || template.templateType || 'TEXT';
}

function canEditTemplate(template) {
  if (!isCloudProvider.value) return true;

  return (
    template.category !== 'AUTHENTICATION' &&
    getTemplateType(template) === 'TEXT'
  );
}

function getButtons(template) {
  const buttonsComp = (template.components || []).find(
    c => c.type === 'BUTTONS'
  );
  if (!buttonsComp?.buttons) return [];
  return buttonsComp.buttons.map(btn => ({
    type: btn.type || 'QUICK_REPLY',
    text: btn.text || '',
    url: btn.url || '',
    phone_number: btn.phone_number || '',
    example: Array.isArray(btn.example)
      ? btn.example[0] || ''
      : btn.example || '',
  }));
}

const statusColor = status => {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'GREEN')
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  if (s === 'PENDING' || s === 'SUBMITTED')
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  if (s === 'REJECTED')
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
};

const statusLabel = status => {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'GREEN') return 'Aprobada';
  if (s === 'PENDING') return 'Pendiente';
  if (s === 'SUBMITTED') return 'Enviada';
  if (s === 'REJECTED') return 'Rechazada';
  if (s === 'DEACTIVATED') return 'Desactivada';
  return status || 'Desconocido';
};

async function loadTemplates() {
  isLoading.value = true;
  try {
    const response = await whatsappChannel.getTemplates(props.inbox.id);
    templates.value = response.data.templates || [];
  } catch {
    useAlert('Error al cargar plantillas');
  } finally {
    isLoading.value = false;
  }
}

async function refreshTemplateData() {
  await Promise.all([loadTemplates(), store.dispatch('inboxes/get')]);
}

async function syncTemplates() {
  isSyncing.value = true;
  try {
    await whatsappChannel.syncTemplates(props.inbox.id);
    useAlert(
      'Sincronización iniciada. Las plantillas se actualizarán en unos segundos.'
    );
    setTimeout(() => refreshTemplateData(), 3000);
  } catch {
    useAlert('Error al sincronizar plantillas');
  } finally {
    isSyncing.value = false;
  }
}

function addButton() {
  if (buttons.value.length >= 10) {
    useAlert('Máximo 10 botones por plantilla');
    return;
  }
  buttons.value.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone_number: '',
    example: '',
  });
}

function removeButton(index) {
  buttons.value.splice(index, 1);
}

function clearMediaFile() {
  mediaFile.value = null;
  form.value.exampleHeader = '';
  if (mediaFileInput.value) mediaFileInput.value.value = '';
}

function selectMediaFile() {
  mediaFileInput.value?.click();
}

function handleMediaFileChange(event) {
  mediaFile.value = event.target.files?.[0] || null;
  form.value.exampleHeader = '';
}

function resetForm() {
  form.value = {
    elementName: '',
    languageCode: 'es',
    category: 'UTILITY',
    templateType: 'TEXT',
    content: '',
    example: '',
    enableSample: true,
    header: '',
    footer: '',
    vertical: '',
    exampleHeader: '',
    mediaUrl: '',
  };
  clearMediaFile();
  buttons.value = [];
  showCreateForm.value = false;
  editingTemplate.value = null;
}

function toggleCreateForm() {
  if (showCreateForm.value) {
    resetForm();
    return;
  }

  resetForm();
  showCreateForm.value = true;
}

function formattedButtons() {
  return buttons.value.map(btn => {
    const button = { type: btn.type, text: btn.text.trim() };
    if (btn.type === 'URL') {
      button.url = btn.url.trim();
      if (btn.example) button.example = [btn.example.trim()];
    } else if (btn.type === 'PHONE_NUMBER') {
      button.phone_number = btn.phone_number.trim();
    } else if (btn.type === 'COPY_CODE') {
      button.example = btn.example.trim();
    }
    return button;
  });
}

function validateForm() {
  if (
    !editingTemplate.value &&
    !/^[a-z0-9_]+$/.test(form.value.elementName.trim())
  ) {
    useAlert(
      'El nombre solo puede contener letras minúsculas, números y guiones bajos.'
    );
    return false;
  }

  if (!form.value.content.trim()) {
    useAlert('El contenido de la plantilla es obligatorio.');
    return false;
  }

  if (
    showMediaUrlField.value &&
    !editingTemplate.value &&
    ((isCloudProvider.value && !mediaFile.value) ||
      (!isCloudProvider.value && !form.value.exampleHeader.trim()))
  ) {
    useAlert(
      isCloudProvider.value
        ? 'Selecciona el archivo multimedia de ejemplo.'
        : 'Ingresa la URL del medio de ejemplo.'
    );
    return false;
  }

  const invalidButton = buttons.value.some(button => {
    if (!button.text.trim()) return true;
    if (button.type === 'URL') return !button.url.trim();
    if (button.type === 'PHONE_NUMBER') return !button.phone_number.trim();
    return false;
  });

  if (invalidButton) {
    useAlert('Completa el texto y los datos requeridos de todos los botones.');
    return false;
  }

  return true;
}

function buildPayload() {
  const payload = { ...form.value };

  if (buttons.value.length > 0) {
    payload.buttons = formattedButtons();
  }

  // Remove empty optional fields
  if (!payload.vertical) delete payload.vertical;
  if (!payload.header) delete payload.header;
  if (!payload.footer) delete payload.footer;
  if (!payload.example) delete payload.example;
  if (!payload.exampleHeader) delete payload.exampleHeader;
  if (!payload.mediaUrl) delete payload.mediaUrl;

  return payload;
}

async function uploadMediaIfNeeded() {
  if (!isCloudProvider.value || !showMediaUrlField.value || !mediaFile.value) {
    return;
  }

  const response = await whatsappChannel.uploadTemplateMedia(
    props.inbox.id,
    mediaFile.value,
    form.value.templateType
  );
  form.value.exampleHeader = response.data.handle;
}

async function createTemplate() {
  if (!validateForm()) return;

  isSaving.value = true;
  try {
    await uploadMediaIfNeeded();
    const payload = buildPayload();
    await whatsappChannel.createTemplate(props.inbox.id, payload);
    useAlert(
      'Plantilla creada y enviada para aprobación. Aparecerá en conversaciones una vez aprobada por Meta.'
    );
    resetForm();
    await refreshTemplateData();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.message ||
      'Error al crear plantilla';
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
}

async function updateTemplate() {
  if (!validateForm()) return;

  isSaving.value = true;
  try {
    const { id } = editingTemplate.value;
    const payload = buildPayload();

    await whatsappChannel.updateTemplate(props.inbox.id, id, payload);
    useAlert('Plantilla actualizada exitosamente');
    resetForm();
    await refreshTemplateData();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.message ||
      'Error al actualizar plantilla';
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
}

function startEdit(template) {
  editingTemplate.value = template;
  form.value = {
    elementName: template.elementName || template.name,
    languageCode: template.languageCode || template.language || 'es',
    category: template.category || 'UTILITY',
    templateType: getTemplateType(template),
    content: getBodyText(template) || '',
    example: '',
    enableSample: true,
    header: getHeaderText(template) || '',
    footer: getFooterText(template) || '',
    vertical: template.vertical || '',
    exampleHeader: getHeaderExample(template) || '',
    mediaUrl: '',
  };

  // Parse existing buttons from components
  buttons.value = getButtons(template);
  showCreateForm.value = true;
}

function confirmDelete(template) {
  deleteTarget.value = template;
  showDeleteConfirm.value = true;
}

async function doDelete() {
  const template = deleteTarget.value;
  showDeleteConfirm.value = false;
  deleteTarget.value = null;

  try {
    const elementName = template.elementName || template.name;
    await whatsappChannel.deleteTemplate(
      props.inbox.id,
      template.id,
      elementName
    );
    useAlert('Plantilla eliminada exitosamente');
    await refreshTemplateData();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.message ||
      'Error al eliminar plantilla';
    useAlert(msg);
  }
}

function cancelDelete() {
  showDeleteConfirm.value = false;
  deleteTarget.value = null;
}

onMounted(() => {
  loadTemplates();
});

watch(
  () => form.value.templateType,
  () => clearMediaFile()
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <div>
    <!-- Delete confirmation dialog -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md mx-4 shadow-xl"
      >
        <h3
          class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2"
        >
          Confirmar eliminación
        </h3>
        <p class="text-slate-600 dark:text-slate-300 mb-4">
          ¿Estás seguro de eliminar la plantilla
          {{ deleteTarget?.elementName || deleteTarget?.name }}?
        </p>
        <div class="flex gap-2 justify-end">
          <NextButton variant="secondary" @click="cancelDelete">
            Cancelar
          </NextButton>
          <NextButton variant="danger" @click="doDelete">Eliminar</NextButton>
        </div>
      </div>
    </div>

    <!-- Header with Sync -->
    <SettingsSection
      title="Plantillas de WhatsApp"
      :sub-title="sectionSubtitle"
    >
      <div class="flex gap-2 items-center mt-2">
        <NextButton :disabled="isSyncing" @click="syncTemplates">
          {{ isSyncing ? 'Sincronizando...' : 'Sincronizar plantillas' }}
        </NextButton>
        <NextButton variant="primary" @click="toggleCreateForm">
          {{ showCreateForm ? 'Cancelar' : 'Crear plantilla' }}
        </NextButton>
      </div>
    </SettingsSection>

    <!-- Create/Edit Form -->
    <div
      v-if="showCreateForm"
      class="mx-8 mt-4 p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
    >
      <h3
        class="text-base font-semibold mb-4 text-slate-900 dark:text-slate-100"
      >
        {{ editingTemplate ? 'Editar plantilla' : 'Crear nueva plantilla' }}
      </h3>

      <p
        v-if="isCloudProvider"
        class="mb-4 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
      >
        Desde aquí puedes crear plantillas de texto, imagen, video y documento
        para utilidad y marketing con Meta Cloud API. Se admiten JPG/PNG hasta 5
        MB, MP4 hasta 16 MB y PDF hasta 100 MB. Las plantillas de autenticación
        se crean en WhatsApp Manager y luego se sincronizan.
      </p>

      <!-- Basic fields -->
      <div class="grid grid-cols-2 gap-4">
        <div v-if="!editingTemplate">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Nombre de la plantilla
          </label>
          <input
            v-model="form.elementName"
            type="text"
            placeholder="order_confirmation"
            class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          />
        </div>
        <div v-if="!editingTemplate">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Idioma
          </label>
          <select
            v-model="form.languageCode"
            class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          >
            <option
              v-for="lang in LANGUAGE_OPTIONS"
              :key="lang.value"
              :value="lang.value"
            >
              {{ lang.label }}
            </option>
          </select>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Categoría
          </label>
          <select
            v-model="form.category"
            class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          >
            <option
              v-for="cat in categoryOptions"
              :key="cat.value"
              :value="cat.value"
            >
              {{ cat.label }}
            </option>
          </select>
        </div>
        <div v-if="!editingTemplate">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Tipo
          </label>
          <select
            v-model="form.templateType"
            class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          >
            <option
              v-for="type in templateTypeOptions"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>
          <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Al seleccionar Imagen, Video o Documento aparecerá debajo el campo
            para cargar el archivo multimedia.
          </p>
        </div>
      </div>

      <!-- Content -->
      <div class="mt-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          Contenido
          <span v-pre class="text-slate-500">
            (usa {{ 1 }}, {{ 2 }} para variables)
          </span>
        </label>
        <textarea
          v-model="form.content"
          rows="3"
          placeholder="Hola {{1}}, tu pedido {{2}} ha sido confirmado."
          class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
        />
      </div>

      <!-- Variables / examples -->
      <div v-if="bodyVariables.length" class="mt-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          Variables
        </label>
        <div class="flex flex-wrap gap-2 mb-2">
          <span
            v-for="variable in bodyVariables"
            :key="variable"
            class="rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-200"
          >
            {{ bodyVariableLabel(variable) }}
          </span>
        </div>
        <p class="mb-2 text-xs text-slate-500 dark:text-slate-400">
          Escribe un ejemplo por cada variable, en el mismo orden y separado por
          comas. Estos ejemplos son solo para la aprobación de Meta; el origen
          de cada dato se selecciona al crear la campaña.
        </p>
        <textarea
          v-model="form.example"
          rows="2"
          placeholder="Juan, #1234"
          class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
        />
      </div>

      <!-- Header / Media URL -->
      <div class="grid grid-cols-2 gap-4 mt-4">
        <div v-if="showHeaderTextField">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Encabezado (opcional)
          </label>
          <input
            v-model="form.header"
            type="text"
            placeholder="Confirmación de pedido"
            class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          />
        </div>
        <div v-if="showMediaUrlField && isCloudProvider">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Archivo multimedia de ejemplo
          </label>
          <input
            ref="mediaFileInput"
            type="file"
            :accept="acceptedMediaTypes"
            class="hidden"
            @change="handleMediaFileChange"
          />
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="px-3 py-2 text-sm border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
              @click="selectMediaFile"
            >
              {{ mediaFile ? 'Cambiar archivo' : 'Seleccionar archivo' }}
            </button>
            <span
              class="min-w-0 text-sm truncate text-slate-600 dark:text-slate-300"
            >
              {{ mediaFile?.name || 'Ningún archivo seleccionado' }}
            </span>
            <button
              v-if="mediaFile"
              type="button"
              class="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
              @click="clearMediaFile"
            >
              Quitar
            </button>
          </div>
        </div>
        <div v-if="showMediaUrlField && !isCloudProvider">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            URL del medio (ejemplo)
          </label>
          <input
            v-model="form.exampleHeader"
            type="url"
            placeholder="https://ejemplo.com/imagen.jpg"
            class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Pie de página (opcional)
          </label>
          <input
            v-model="form.footer"
            type="text"
            placeholder="Gracias por tu compra"
            class="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          />
        </div>
      </div>

      <!-- Buttons section -->
      <div class="mt-4">
        <div class="flex items-center justify-between mb-2">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Botones (opcional)
          </label>
          <button
            type="button"
            class="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            @click="addButton"
          >
            + Agregar botón
          </button>
        </div>
        <div
          v-if="buttons.length === 0"
          class="text-sm text-slate-400 dark:text-slate-500"
        >
          Sin botones. Haz clic en Agregar botón para añadir botones de
          respuesta rápida, enlace, teléfono o copiar código.
        </div>
        <div
          v-for="(btn, index) in buttons"
          :key="index"
          class="relative flex flex-col gap-3 p-3 mb-3 bg-white border rounded-lg border-slate-200 shadow-sm dark:bg-slate-700/50 dark:border-slate-600"
        >
          <div
            class="grid grid-cols-1 gap-3 pr-11 sm:grid-cols-[12rem_minmax(0,1fr)]"
          >
            <label
              class="flex flex-col gap-1 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Tipo de botón
              <select
                v-model="btn.type"
                class="w-full h-10 px-3 text-sm text-slate-900 bg-white border rounded-lg border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100 dark:focus:ring-blue-900/40"
              >
                <option
                  v-for="opt in buttonTypeOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </label>
            <label
              class="flex min-w-0 flex-col gap-1 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Texto visible del botón
              <input
                v-model="btn.text"
                type="text"
                placeholder="Ejemplo: Ver pedido"
                class="w-full h-10 min-w-0 px-3 text-sm text-slate-900 placeholder:text-slate-400 bg-white border rounded-lg border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-900/40"
              />
            </label>
          </div>

          <div
            v-if="btn.type === 'URL'"
            class="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            <label
              class="flex flex-col gap-1 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              URL de destino
              <input
                v-model="btn.url"
                type="url"
                placeholder="https://ejemplo.com/pedido/{{1}}"
                class="w-full h-10 px-3 text-sm text-slate-900 placeholder:text-slate-400 bg-white border rounded-lg border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-900/40"
              />
            </label>
            <label
              class="flex flex-col gap-1 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Ejemplo de URL
              <input
                v-model="btn.example"
                type="url"
                placeholder="https://ejemplo.com/pedido/1234"
                class="w-full h-10 px-3 text-sm text-slate-900 placeholder:text-slate-400 bg-white border rounded-lg border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-900/40"
              />
            </label>
          </div>

          <label
            v-if="btn.type === 'PHONE_NUMBER'"
            class="flex flex-col gap-1 text-sm font-medium text-slate-700 dark:text-slate-200 sm:max-w-md"
          >
            Número de teléfono
            <input
              v-model="btn.phone_number"
              type="tel"
              placeholder="+57 300 1234567"
              class="w-full h-10 px-3 text-sm text-slate-900 placeholder:text-slate-400 bg-white border rounded-lg border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-900/40"
            />
          </label>

          <label
            v-if="btn.type === 'COPY_CODE'"
            class="flex flex-col gap-1 text-sm font-medium text-slate-700 dark:text-slate-200 sm:max-w-md"
          >
            Código de ejemplo
            <input
              v-model="btn.example"
              type="text"
              placeholder="DESCUENTO20"
              class="w-full h-10 px-3 text-sm text-slate-900 placeholder:text-slate-400 bg-white border rounded-lg border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:bg-slate-800 dark:border-slate-500 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-900/40"
            />
          </label>

          <button
            type="button"
            aria-label="Eliminar botón"
            class="absolute inline-flex items-center justify-center text-red-600 rounded-lg right-3 top-3 size-9 hover:bg-red-50 hover:text-red-800 dark:text-red-400 dark:hover:bg-red-950/40 dark:hover:text-red-300"
            @click="removeButton(index)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-2 mt-4">
        <NextButton
          :disabled="isSaving"
          :is-loading="isSaving"
          @click="editingTemplate ? updateTemplate() : createTemplate()"
        >
          {{ editingTemplate ? 'Actualizar plantilla' : 'Crear plantilla' }}
        </NextButton>
        <NextButton variant="secondary" @click="resetForm">Cancelar</NextButton>
      </div>
    </div>

    <!-- Filters -->
    <div class="mx-8 mt-4 flex gap-4 items-center">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Buscar plantilla..."
        class="flex-1 px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
      />
      <select
        v-model="categoryFilter"
        class="px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
      >
        <option value="all">Todas</option>
        <option value="UTILITY">Utilidad</option>
        <option value="MARKETING">Marketing</option>
        <option value="AUTHENTICATION">Autenticación</option>
      </select>
    </div>

    <!-- Template List -->
    <div class="mx-8 mt-4">
      <div v-if="isLoading" class="text-center py-8 text-slate-500">
        Cargando plantillas...
      </div>
      <div
        v-else-if="filteredTemplates.length === 0"
        class="text-center py-8 text-slate-500"
      >
        No se encontraron plantillas. Crea una nueva plantilla o sincroniza las
        existentes desde {{ providerLabel }}.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-200 dark:border-slate-700">
              <th
                class="text-left py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Nombre
              </th>
              <th
                class="text-left py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Idioma
              </th>
              <th
                class="text-left py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Tipo
              </th>
              <th
                class="text-left py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Categoría
              </th>
              <th
                class="text-left py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Contenido
              </th>
              <th
                class="text-left py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Botones
              </th>
              <th
                class="text-left py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Estado
              </th>
              <th
                class="text-right py-2 px-3 text-slate-600 dark:text-slate-400"
              >
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="template in filteredTemplates"
              :key="template.id || template.elementName"
              class="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/50"
            >
              <td
                class="py-2 px-3 font-medium text-slate-900 dark:text-slate-100"
              >
                {{ template.elementName || template.name }}
              </td>
              <td class="py-2 px-3 text-slate-700 dark:text-slate-300">
                {{ template.languageCode || template.language }}
              </td>
              <td class="py-2 px-3 text-slate-700 dark:text-slate-300">
                {{ getTemplateType(template) }}
              </td>
              <td class="py-2 px-3 text-slate-700 dark:text-slate-300">
                {{ template.category }}
              </td>
              <td
                class="py-2 px-3 text-slate-700 dark:text-slate-300 max-w-xs truncate"
              >
                {{ getBodyText(template) }}
              </td>
              <td class="py-2 px-3 text-slate-700 dark:text-slate-300">
                <span
                  v-if="getButtons(template).length"
                  class="inline-flex items-center gap-1"
                >
                  <span
                    class="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded text-xs"
                  >
                    {{ getButtons(template).length }} botón{{
                      getButtons(template).length > 1 ? 'es' : ''
                    }}
                  </span>
                </span>
                <span v-else class="text-slate-400 dark:text-slate-500">—</span>
              </td>
              <td class="py-2 px-3">
                <span
                  class="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="statusColor(template.status)"
                >
                  {{ statusLabel(template.status) }}
                </span>
              </td>
              <td class="py-2 px-3 text-right">
                <button
                  v-if="canEditTemplate(template)"
                  class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-3 text-xs"
                  @click="startEdit(template)"
                >
                  Editar
                </button>
                <button
                  class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs"
                  @click="confirmDelete(template)"
                >
                  Eliminar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>
